'use client';

import { getToken } from 'firebase/app-check';
import { signInWithCustomToken } from 'firebase/auth';
import { resolvePostSessionSyncRedirect, syncServerSessionForUser } from '@/lib/auth-session';
import { appCheck, auth, authPersistenceReady } from '@/lib/firebase';
import { normalizeSafeInternalPath } from '@/lib/safe-internal-path';

type DesktopProvider = 'google' | 'okta' | 'magic-link';

interface DesktopAuthRequest {
  requestId: string;
  verifier: string;
  browserPath: string;
  expiresAtMs: number;
}

let activeDesktopAuth: Promise<string> | null = null;

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

async function sha256Base64Url(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  return bytesToBase64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)));
}

function randomVerifier(): string {
  return bytesToBase64Url(crypto.getRandomValues(new Uint8Array(32)));
}

function requireAppCheck() {
  if (!appCheck) throw new Error('App security could not be initialized.');
  return appCheck;
}

async function responsePayload(response: Response): Promise<Record<string, unknown>> {
  try {
    const value = await response.json();
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function payloadError(payload: Record<string, unknown>, fallback: string): Error {
  return new Error(typeof payload.error === 'string' ? payload.error : fallback);
}

async function createDesktopRequest(
  provider: DesktopProvider,
  redirectPath: string
): Promise<DesktopAuthRequest> {
  const verifier = randomVerifier();
  const challenge = await sha256Base64Url(verifier);
  const appCheckToken = await getToken(requireAppCheck(), false);
  const response = await fetch('/api/auth/desktop/start', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Firebase-AppCheck': appCheckToken.token,
    },
    body: JSON.stringify({ provider, redirectPath, challenge }),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw payloadError(payload, 'Could not start desktop authentication.');

  const requestId = typeof payload.requestId === 'string' ? payload.requestId : '';
  const browserPath = normalizeSafeInternalPath(payload.browserPath);
  const expiresAtMs = typeof payload.expiresAt === 'string' ? Date.parse(payload.expiresAt) : NaN;
  if (!requestId || !browserPath?.startsWith('/desktop-auth?') || !Number.isFinite(expiresAtMs)) {
    throw new Error('Attune returned an invalid desktop authentication response.');
  }

  return { requestId, verifier, browserPath, expiresAtMs };
}

async function wait(ms: number): Promise<void> {
  await new Promise(resolve => globalThis.setTimeout(resolve, ms));
}

async function redeemDesktopRequest(
  desktopRequest: DesktopAuthRequest,
  fallbackRedirect: string
): Promise<string> {
  const appCheckToken = await getToken(requireAppCheck(), false);

  while (Date.now() < desktopRequest.expiresAtMs) {
    const response = await fetch('/api/auth/desktop/redeem', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Firebase-AppCheck': appCheckToken.token,
      },
      body: JSON.stringify({
        requestId: desktopRequest.requestId,
        verifier: desktopRequest.verifier,
      }),
    });
    const payload = await responsePayload(response);

    if (response.status === 202) {
      await wait(2_000);
      continue;
    }
    if (!response.ok) throw payloadError(payload, 'Desktop authentication failed.');
    if (typeof payload.customToken !== 'string') {
      throw new Error('Attune did not return a desktop sign-in token.');
    }

    await authPersistenceReady;
    const credential = await signInWithCustomToken(auth, payload.customToken);
    const sessionResult = await syncServerSessionForUser(credential.user, { forceRefresh: true });
    const serverTarget = normalizeSafeInternalPath(payload.targetUrl, fallbackRedirect);
    return resolvePostSessionSyncRedirect(serverTarget || fallbackRedirect, sessionResult);
  }

  throw new Error('Desktop authentication expired. Start sign-in again.');
}

function runSingleDesktopAuth(operation: () => Promise<string>): Promise<string> {
  if (activeDesktopAuth) return activeDesktopAuth;
  activeDesktopAuth = operation().finally(() => {
    activeDesktopAuth = null;
  });
  return activeDesktopAuth;
}

export function isAttuneDesktopShell(): boolean {
  return typeof window !== 'undefined' && window.attuneDesktop?.platform === 'macos';
}

export function startDesktopProviderSignIn(
  provider: Exclude<DesktopProvider, 'magic-link'>,
  redirectPath: string
): Promise<string> {
  return runSingleDesktopAuth(async () => {
    const desktopRequest = await createDesktopRequest(provider, redirectPath);
    const browserUrl = new URL(desktopRequest.browserPath, window.location.origin).toString();
    const opened = await window.attuneDesktop?.openAuth(browserUrl);
    if (!opened) throw new Error('Could not open your browser for sign-in.');
    return redeemDesktopRequest(desktopRequest, redirectPath);
  });
}

export function startDesktopMagicLinkSignIn(
  email: string,
  redirectPath: string,
  onLinkSent: () => void
): Promise<string> {
  return runSingleDesktopAuth(async () => {
    const desktopRequest = await createDesktopRequest('magic-link', redirectPath);
    const appCheckToken = await getToken(requireAppCheck(), false);
    const response = await fetch('/api/auth/magic-link', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Firebase-AppCheck': appCheckToken.token,
      },
      body: JSON.stringify({ email, redirectUrl: desktopRequest.browserPath }),
    });
    const payload = await responsePayload(response);
    if (!response.ok) throw payloadError(payload, 'Failed to send sign-in link.');
    onLinkSent();
    return redeemDesktopRequest(desktopRequest, redirectPath);
  });
}
